import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Button,
  Label,
  Textarea,
  Input,
  toast,
} from "@mindstonehq/ui";
import { graphql } from "~/graphql/codegen";
import { useQuery, useMutation } from "@apollo/client/react";
import metrics from "~/utils/metrics";
import { classNames, formatDate } from "~/utils";
import type {
  TimeEntryType,
  AiUsageQuery,
  AiUsageQueryVariables,
  AiUsageLastLogDateQuery,
  AiUsageLastLogDateQueryVariables,
  LogAiUsageMutation,
  LogAiUsageMutationVariables,
} from "~/graphql/codegen/graphql";
import { useCurrentSpace } from "~/hooks/context-hook";
import { OptionButton } from "~/components/common/option-button";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import type { MessageDescriptor } from "@lingui/core";

const AI_USAGE_QUERY = graphql(`
  query AiUsage {
    currentUser {
      aiUsage {
        timeSaved {
          monthSince
          value
          entryType
        }
        timeSpent {
          monthSince
          value
          entryType
        }
        savedVsSpent {
          monthSince
          value
          entryType
        }
      }
    }
    aiUsage {
      timeSaved {
        monthSince
        value
        entryType
      }
      timeSpent {
        monthSince
        value
        entryType
      }
      savedVsSpent {
        monthSince
        value
        entryType
      }
    }
  }
`);

const AI_USAGE_LAST_LOG_DATE_QUERY = graphql(`
  query AiUsageLastLogDate {
    currentUser {
      aiUsage {
        lastLogDate
      }
    }
  }
`);

const LOG_AI_USAGE_MUTATION = graphql(`
  mutation LogAiUsage($input: LogAIUsageInput!) {
    logAIUsage(input: $input)
  }
`);

// Option buttons for time selection
function buildTimeOptions(t: (descriptor: MessageDescriptor) => string) {
  return [
    { id: "0", label: t(msg`0 hours`), value: 0 },
    { id: "0-1", label: t(msg`0-1 hours`), value: 1 },
    { id: "2-4", label: t(msg`2-4 hours`), value: 3 },
    { id: "5-10", label: t(msg`5-10 hours`), value: 7 },
    { id: "11-20", label: t(msg`11-20 hours`), value: 15 },
    { id: "21-30", label: t(msg`21-30 hours`), value: 25 },
    { id: "other", label: t(msg`Other`) },
  ];
}

function buildLearningTimeOptions(t: (descriptor: MessageDescriptor) => string) {
  return [
    { id: "0", label: t(msg`0 hours`), value: 0 },
    { id: "0-1", label: t(msg`0-1 hours`), value: 1 },
    { id: "2-4", label: t(msg`2-4 hours`), value: 3 },
    { id: "5-10", label: t(msg`5-10 hours`), value: 7 },
    { id: "11-20", label: t(msg`11-20 hours`), value: 15 },
    { id: "21-40", label: t(msg`21-40 hours`), value: 30 },
    { id: "other", label: t(msg`Other`) },
  ];
}

interface LogAIUsageDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function LogAIUsageDialog({ open, onOpenChange }: LogAIUsageDialogProps) {
  const { t } = useLingui();
  const timeOptions = React.useMemo(() => buildTimeOptions(t), [t]);
  const learningTimeOptions = React.useMemo(
    () => buildLearningTimeOptions(t),
    [t],
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [timeSaved, setTimeSaved] = useState("");
  const [timeSpent, setTimeSpent] = useState("");
  const [focusAreas, setFocusAreas] = useState("");
  const [customTimeSaved, setCustomTimeSaved] = useState("");
  const [customTimeSpent, setCustomTimeSpent] = useState("");
  const [activitiesDescription, setActivitiesDescription] = useState("");
  const [hasTrackedDescriptionMetrics, setHasTrackedDescriptionMetrics] = useState(false);
  const getTimeSavedHours = (): number => {
    if (!timeSaved) return 0;
    if (timeSaved === "other") {
      const val = parseInt(customTimeSaved);
      return isNaN(val) ? 0 : val;
    }
    const option = timeOptions.find((o) => o.id === timeSaved);
    return option && typeof option.value === "number" ? option.value : 0;
  };
  const [errors, setErrors] = useState<{
    timeSaved?: string;
    timeSpent?: string;
    customTimeSaved?: string;
    customTimeSpent?: string;
  }>({});
  const [logAiUsageMutation] = useMutation<LogAiUsageMutation, LogAiUsageMutationVariables>(LOG_AI_USAGE_MUTATION);
  const currentSpace = useCurrentSpace();

  const { data } = useQuery<AiUsageLastLogDateQuery, AiUsageLastLogDateQueryVariables>(AI_USAGE_LAST_LOG_DATE_QUERY);
  const { data: usageData } = useQuery<AiUsageQuery, AiUsageQueryVariables>(AI_USAGE_QUERY);
  const lastCheckInDate = data?.currentUser?.aiUsage?.lastLogDate
    ? formatDate(new Date(data.currentUser.aiUsage.lastLogDate))
    : undefined;

  const lastSavedHours = usageData?.currentUser?.aiUsage?.timeSaved?.length
    ? usageData.currentUser.aiUsage.timeSaved[usageData.currentUser.aiUsage.timeSaved.length - 1].value
    : undefined;
  const lastSpentHours = usageData?.currentUser?.aiUsage?.timeSpent?.length
    ? usageData.currentUser.aiUsage.timeSpent[usageData.currentUser.aiUsage.timeSpent.length - 1].value
    : undefined;

  // Check if form has valid time entries
  const isFormComplete = () => {
    const hasTimeSaved = timeSaved && (timeSaved !== "other" || (timeSaved === "other" && customTimeSaved));
    const hasTimeSpent = timeSpent && (timeSpent !== "other" || (timeSpent === "other" && customTimeSpent));
    return hasTimeSaved && hasTimeSpent;
  };

  const validateForm = () => {
    const newErrors: {
      timeSaved?: string;
      timeSpent?: string;
      customTimeSaved?: string;
      customTimeSpent?: string;
    } = {};

    if (!timeSaved) {
      newErrors.timeSaved = t(msg`Please select an option`);
    } else if (timeSaved === "other" && customTimeSaved) {
      const hours = parseInt(customTimeSaved, 10);
      if (isNaN(hours) || hours <= 0 || hours > 720) {
        newErrors.customTimeSaved = t(
          msg`Please enter a number between 1 and 720 hours`,
        );
      }
    } else if (timeSaved === "other" && !customTimeSaved) {
      newErrors.customTimeSaved = t(msg`Please enter number of hours`);
    }

    if (!timeSpent) {
      newErrors.timeSpent = t(msg`Please select an option`);
    } else if (timeSpent === "other" && customTimeSpent) {
      const hours = parseInt(customTimeSpent, 10);
      if (isNaN(hours) || hours <= 0 || hours > 720) {
        newErrors.customTimeSpent = t(
          msg`Please enter a number between 1 and 720 hours`,
        );
      }
    } else if (timeSpent === "other" && !customTimeSpent) {
      newErrors.customTimeSpent = t(msg`Please enter number of hours`);
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm() || isSubmitting) return;

    setIsSubmitting(true);

    try {
      // Helper to get numeric value from options
      const getOptionValue = (id: string, options: { id: string; value?: number }[]): number => {
        const found = options.find((o) => o.id === id);
        return found && typeof found.value === "number" ? found.value : 0;
      };

      // Calculate numeric hours to send to backend
      const timeSavedHours = timeSaved === "other" ? parseInt(customTimeSaved) : getOptionValue(timeSaved, timeOptions);
      const timeSpentHours =
        timeSpent === "other" ? parseInt(customTimeSpent) : getOptionValue(timeSpent, learningTimeOptions);

      // Track the submission event
      metrics.track("submit_logai_dialog", {
        time_saved_hours: timeSavedHours,
        time_spent_hours: timeSpentHours,
        has_focus_areas: !!focusAreas,
        focus_areas_length: focusAreas.length,
      });

      await logAiUsageMutation({
        variables: {
          input: {
            mindspaceId: currentSpace.id,
            timeSavedHours,
            timeSpentHours,
            focusText: focusAreas || null,
            aiUsageActivities: activitiesDescription || null,
          },
        },
        refetchQueries: ["AiUsage", "AiUsageLastLogDate"],
        awaitRefetchQueries: true,
      });

      // Show success message
      toast.success(t(msg`AI usage logged successfully`), {
        description: t(msg`Thank you for sharing your AI usage data.`),
      });

      // Reset form and close dialog
      resetForm();
      onOpenChange(false);
    } catch (error) {
      console.error("Error submitting AI usage log:", error);
      toast.error(t(msg`Failed to log AI usage`), {
        description: t(msg`Please try again later.`),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setTimeSaved("");
    setTimeSpent("");
    setFocusAreas("");
    setCustomTimeSaved("");
    setCustomTimeSpent("");
    setActivitiesDescription("");
    setHasTrackedDescriptionMetrics(false);
    setErrors({});
  };

  const handleCancel = () => {
    metrics.track("logai_dialog_cancelled");
    resetForm();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-[600px] md:max-w-[900px] max-h-[90vh] overflow-y-auto px-4 sm:px-6"
        aria-describedby="dialog-description"
      >
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold text-foreground tracking-tight">
            <Trans>Keep track how AI is helping you</Trans>
          </DialogTitle>
          <p id="dialog-description" className="text-base text-muted-foreground leading-7">
            <Trans>
              Your answers update your profile and help you and your team
              understand how AI is supporting your work and learning.
            </Trans>
          </p>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Time Saved Question */}
          <fieldset className="space-y-3">
            <legend className="sr-only">
              <Trans>Time saved using AI</Trans>
            </legend>
            <div className="space-y-1">
              <Label className="text-lg font-semibold text-foreground leading-7">
                {lastCheckInDate ? (
                  <Trans>
                    How many additional hours per week is AI saving you since
                    your last check-in?
                  </Trans>
                ) : (
                  <Trans>
                    How many hours per week is AI saving you today?
                  </Trans>
                )}
                {lastCheckInDate && (
                  <>
                    &nbsp;
                    <span className="text-base font-normal text-foreground whitespace-nowrap">
                      <Trans>
                        (your last check-in was on {lastCheckInDate} –{" "}
                        {lastSavedHours ?? 0} h/w)
                      </Trans>
                    </span>
                  </>
                )}
              </Label>
              <p className="text-sm text-muted-foreground font-normal leading-tight">
                <Trans>
                  Think about time saved on tasks like writing, summarising,
                  planning, or admin.
                </Trans>
              </p>
            </div>

            <div
              className={classNames(
                "grid gap-2",
                timeSaved === "other"
                  ? "grid-cols-[repeat(6,minmax(0,1fr))_180px] max-sm:grid-cols-[1fr_180px] max-lg:grid-cols-[repeat(4,minmax(0,1fr))_180px]"
                  : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-7",
              )}
              role="radiogroup"
              aria-labelledby="time-saved-label"
            >
              <AnimatePresence mode="wait">
                {timeOptions.map((option) => {
                  if (option.id === "other" && timeSaved === "other") {
                    return (
                      <motion.div
                        key={option.id}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="w-full"
                      >
                        <Input
                          type="number"
                          placeholder={t(msg`no. of hours`)}
                          value={customTimeSaved}
                          onChange={(e) => {
                            setCustomTimeSaved(e.target.value);
                            setErrors((prev) => ({
                              ...prev,
                              customTimeSaved: undefined,
                            }));
                          }}
                          className="h-10 px-2 py-0 text-sm font-normal leading-5 focus:ring-2 focus:ring-primary w-full border border-required"
                          min="1"
                          max="720"
                          aria-label={t(msg`Enter number of hours saved`)}
                          autoFocus
                        />
                      </motion.div>
                    );
                  }
                  return (
                    <OptionButton
                      key={option.id}
                      id={option.id}
                      label={option.label}
                      selected={timeSaved === option.id}
                      onClick={() => {
                        setTimeSaved(option.id);
                        if (option.id !== "other") {
                          setCustomTimeSaved("");
                        }
                        setErrors((prev) => ({
                          ...prev,
                          timeSaved: undefined,
                          customTimeSaved: undefined,
                        }));
                      }}
                    />
                  );
                })}
              </AnimatePresence>
            </div>
            {errors.timeSaved && (
              <p className="text-sm text-destructive" role="alert" aria-live="polite">
                {errors.timeSaved}
              </p>
            )}
            {errors.customTimeSaved && (
              <p className="text-sm text-destructive" role="alert" aria-live="polite">
                {errors.customTimeSaved}
              </p>
            )}
          </fieldset>

          {/* Time Spent Learning Question */}
          <fieldset className="space-y-3">
            <legend className="sr-only">
              <Trans>Time spent learning AI</Trans>
            </legend>
            <div className="space-y-1">
              <Label className="text-lg font-semibold text-foreground leading-7">
                {lastCheckInDate ? (
                  <Trans>
                    How much time did you spend learning or experimenting with
                    AI since your last check-in?
                  </Trans>
                ) : (
                  <Trans>
                    How much time did you spend learning or experimenting with
                    AI since last month?
                  </Trans>
                )}
                {lastCheckInDate && (
                  <>
                    &nbsp;
                    <span className="text-base font-normal text-foreground whitespace-nowrap">
                      <Trans>
                        (your last check-in was on {lastCheckInDate} –{" "}
                        {lastSpentHours ?? 0} h/w)
                      </Trans>
                    </span>
                  </>
                )}
              </Label>
              {(timeSpent || customTimeSpent) && (
                <p className="text-sm text-muted-foreground font-normal leading-tight">
                  <Trans>
                    This includes time spent in the program or trying out tools
                    on your own.
                  </Trans>
                </p>
              )}
            </div>

            <div
              className={classNames(
                "grid gap-2",
                timeSpent === "other"
                  ? "grid-cols-[repeat(6,minmax(0,1fr))_180px] max-sm:grid-cols-[1fr_180px] max-lg:grid-cols-[repeat(4,minmax(0,1fr))_180px]"
                  : "grid-cols-2 sm:grid-cols-3 lg:grid-cols-7",
              )}
              role="radiogroup"
              aria-labelledby="time-spent-label"
            >
              <AnimatePresence mode="wait">
                {learningTimeOptions.map((option) => {
                  if (option.id === "other" && timeSpent === "other") {
                    return (
                      <motion.div
                        key={option.id}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.3, ease: "easeInOut" }}
                        className="w-full"
                      >
                        <Input
                          type="number"
                          placeholder={t(msg`no. of hours`)}
                          value={customTimeSpent}
                          onChange={(e) => {
                            setCustomTimeSpent(e.target.value);
                            setErrors((prev) => ({
                              ...prev,
                              customTimeSpent: undefined,
                            }));
                          }}
                          className="h-10 px-3 py-0 text-sm font-normal leading-5 focus:ring-2 focus:ring-primary w-full border border-required"
                          min="1"
                          max="720"
                          aria-label={t(
                            msg`Enter number of hours spent learning`,
                          )}
                          autoFocus
                        />
                      </motion.div>
                    );
                  }
                  return (
                    <OptionButton
                      key={option.id}
                      id={option.id}
                      label={option.label}
                      selected={timeSpent === option.id}
                      onClick={() => {
                        setTimeSpent(option.id);
                        if (option.id !== "other") {
                          setCustomTimeSpent("");
                        }
                        setErrors((prev) => ({
                          ...prev,
                          timeSpent: undefined,
                          customTimeSpent: undefined,
                        }));
                      }}
                    />
                  );
                })}
              </AnimatePresence>
            </div>
            {errors.timeSpent && (
              <p className="text-sm text-destructive" role="alert" aria-live="polite">
                {errors.timeSpent}
              </p>
            )}
            {errors.customTimeSpent && (
              <p className="text-sm text-destructive" role="alert" aria-live="polite">
                {errors.customTimeSpent}
              </p>
            )}
          </fieldset>

          {/* Focus Areas Question */}
          <div className="space-y-3">
            <Label htmlFor="focusAreas" className="text-lg font-semibold text-foreground leading-7">
              <Trans>
                What have you been able to focus on with that saved time?{" "}
                <span className="text-muted-foreground">(optional)</span>
              </Trans>
            </Label>
            <Textarea
              id="focusAreas"
              value={focusAreas}
              onChange={(e) => setFocusAreas(e.target.value)}
              placeholder={t(
                msg`e.g., strategic planning, deep work, learning new skills, client projects`,
              )}
              className="min-h-10 resize-none border border-muted"
              rows={2}
              aria-describedby="focusAreas-hint"
            />
            <p id="focusAreas-hint" className="sr-only">
              <Trans>
                Optional field to describe what you focused on with the time
                saved
              </Trans>
            </p>
          </div>

          {/* AI Usage Activities Description (optional) */}
          <div className="space-y-3">
            <div className="space-y-3">
              <Label htmlFor="activitiesDescription" className="text-lg font-semibold text-foreground leading-7">
                <Trans>
                  Can you describe what helped you save this time?{" "}
                  <span className="text-muted-foreground">(optional)</span>
                </Trans>
              </Label>
              <Textarea
                id="activitiesDescription"
                value={activitiesDescription}
                onChange={(e) => {
                  setActivitiesDescription(e.target.value);
                  if (e.target.value.length > 0 && !hasTrackedDescriptionMetrics) {
                    metrics.track("ai_growth_description_added");
                    setHasTrackedDescriptionMetrics(true);
                  }
                }}
                placeholder={t(
                  msg`e.g., using AI tooling, automating summarization with ChatGPT, using AI to plan a project`,
                )}
                className="min-h-10 resize-none border border-muted"
                rows={2}
              />
            </div>
          </div>

          <DialogFooter className="flex flex-row justify-end gap-3">
            <Button type="button" variant="outline" onClick={handleCancel} disabled={isSubmitting}>
              <Trans>Cancel</Trans>
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || !isFormComplete()}
              className="bg-primary hover:bg-primary-hover"
              aria-describedby={isSubmitting ? "submit-status" : undefined}
            >
              {isSubmitting ? <Trans>Saving...</Trans> : <Trans>Save log</Trans>}
              {isSubmitting && (
                <span id="submit-status" className="sr-only">
                  <Trans>Submitting your AI usage data</Trans>
                </span>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
